import { gsap } from "gsap";
import type { CardController } from "../card/CardController";
import type { DragController } from "../card/DragController";

export type WarpDirection = "in" | "out";
export type SlideAxis = "left" | "right" | "none";

export interface CardWarpDeps {
  card: CardController;
  dragController: DragController;
  overlay: HTMLElement;
  /**
   * The wrapper element transformed during nav transitions. Lives outside
   * `.card-3d` so its CSS float/idle animation does not steal the GSAP-set
   * transform on this element.
   */
  warpEl: HTMLElement;
}

/**
 * Drives the home → page transition (and back) entirely with CSS transforms
 * on the card root, plus an opacity tween on the page overlay.
 *
 * Public API matches what Router expects: `enter(route, axis?)` and
 * `exit(axis?)`. The optional axis tilts the card's tumble to match the
 * on-screen route order (about=left, link=right) so the card flies away
 * (and returns) on the side of the page it leads to.
 */
export class CardWarp {
  private readonly deps: CardWarpDeps;
  private current: gsap.core.Timeline | null = null;
  private state: "home" | "page" = "home";

  constructor(deps: CardWarpDeps) {
    this.deps = deps;
  }

  async enter(_routeName: string, axis: SlideAxis = "none"): Promise<void> {
    if (this.state === "page") return;
    this.state = "page";
    return this.runTimeline("in", axis);
  }

  async exit(axis: SlideAxis = "none"): Promise<void> {
    if (this.state === "home") return;
    this.state = "home";
    return this.runTimeline("out", axis);
  }

  private runTimeline(direction: WarpDirection, axis: SlideAxis): Promise<void> {
    const { card, dragController, overlay, warpEl } = this.deps;
    this.current?.kill();
    dragController.disable();
    card.externalControl = true;
    card.suspendFloat();

    return new Promise<void>((resolve) => {
      const tl = gsap.timeline({
        onComplete: () => {
          if (direction === "out") {
            card.externalControl = false;
            dragController.enable();
            card.resumeFloat();
          }
          this.current = null;
          resolve();
        },
      });

      const start = card.rotation;
      const rotState = { x: start.x, y: start.y, z: start.z };

      // Distance the card needs to travel to fully clear the viewport.
      const exitOffset = window.innerHeight + 240;

      // Reset the pivot's user rotation back to neutral so the card lands
      // upright when it returns. The flashy tumbling spin lives on the warp
      // wrapper below; keeping the inner pivot calm avoids visual chaos.
      tl.to(rotState, {
        x: 0,
        y: Math.round(start.y / (Math.PI * 2)) * (Math.PI * 2),
        z: 0,
        duration: 0.9,
        ease: direction === "in" ? "power2.in" : "power3.out",
        onUpdate: () => card.setRotation(rotState.x, rotState.y, rotState.z),
      }, 0);

      // Settle any pinch-pan state back to neutral while the card is
      // flying away. Only matters on enter; on exit the pivot transform
      // is already at the home pose.
      if (direction === "in") {
        const startTransform = card.transform;
        const transState = {
          tx: startTransform.tx,
          ty: startTransform.ty,
          scl: startTransform.scl,
        };
        tl.to(transState, {
          tx: 0,
          ty: 0,
          scl: 1,
          duration: 0.9,
          ease: "power2.in",
          onUpdate: () =>
            card.setTransform(transState.tx, transState.ty, transState.scl),
        }, 0);
      }

      // Tumbling exit on the warp wrapper: spins around Z while tipping
      // forward on X, then drops off-screen. The return tween reverses the
      // exact same trajectory. Axis tilts the X destination so the card
      // departs to the side matching the route order (about=left, link=right).
      const xMagnitude = 180;
      const xOffset = axis === "left" ? -xMagnitude : axis === "right" ? xMagnitude : 0;
      const rotationSign = axis === "right" ? 1 : -1;

      tl.to(warpEl, {
        x: direction === "in" ? xOffset : 0,
        y: direction === "in" ? exitOffset : 0,
        scale: direction === "in" ? 0.7 : 1,
        rotation: direction === "in" ? 260 * rotationSign : 0,
        rotationX: direction === "in" ? 28 : 0,
        opacity: direction === "in" ? 0 : 1,
        duration: 0.9,
        ease: direction === "in" ? "power2.in" : "power3.out",
      }, 0);

      tl.fromTo(
        overlay,
        { opacity: direction === "in" ? 0 : 1 },
        {
          opacity: direction === "in" ? 1 : 0,
          duration: 0.45,
          ease: "power2.out",
          onStart: () => {
            if (direction === "in") {
              overlay.removeAttribute("hidden");
              overlay.dataset["active"] = "true";
            }
          },
          onComplete: () => {
            if (direction === "out") {
              overlay.dataset["active"] = "false";
              overlay.setAttribute("hidden", "");
              overlay.replaceChildren();
            }
          },
        },
        direction === "in" ? 0.55 : 0,
      );

      this.current = tl;
    });
  }
}
